import { useQuery } from '@tanstack/react-query';
import { startOfDay, addDays } from 'date-fns';
import { supabase } from '@/lib/supabase';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { queryKeys } from '@/lib/query-keys';

interface ActivityCounts {
  missedCount: number;
  todayCount: number;
  upcomingCount: number;
  isLoading: boolean;
}

async function fetchActivityCounts(workspaceId: string): Promise<{
  missedCount: number;
  todayCount: number;
  upcomingCount: number;
}> {
  const now = new Date();
  const todayStart = startOfDay(now).toISOString();
  const tomorrowStart = startOfDay(addDays(now, 1)).toISOString();

  // Missed/Today/Upcoming surface outstanding **scheduled** tasks. Activities
  // logged after the fact (scheduled=false) live in the contact's history
  // and shouldn't pollute the calendar's to-do views even if `completed`
  // hasn't been flipped on them.
  const [missedResult, todayResult, upcomingResult] = await Promise.all([
    supabase
      .from('activities')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('scheduled', true)
      .eq('completed', false)
      .lt('start_date', todayStart),

    supabase
      .from('activities')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('scheduled', true)
      .eq('completed', false)
      .gte('start_date', todayStart)
      .lt('start_date', tomorrowStart),

    supabase
      .from('activities')
      .select('id', { count: 'exact', head: true })
      .eq('workspace_id', workspaceId)
      .eq('scheduled', true)
      .eq('completed', false)
      .gte('start_date', tomorrowStart),
  ]);

  if (missedResult.error) throw missedResult.error;
  if (todayResult.error) throw todayResult.error;
  if (upcomingResult.error) throw upcomingResult.error;

  return {
    missedCount: missedResult.count ?? 0,
    todayCount: todayResult.count ?? 0,
    upcomingCount: upcomingResult.count ?? 0,
  };
}

export function useActivityCounts(): ActivityCounts {
  const { currentWorkspace } = useWorkspaces();
  const workspaceId = currentWorkspace?.id ?? null;

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.activities.counts(workspaceId ?? undefined),
    queryFn: () => fetchActivityCounts(workspaceId!),
    enabled: !!workspaceId,
    staleTime: 60 * 1000, // 60 seconds
  });

  return {
    missedCount: data?.missedCount ?? 0,
    todayCount: data?.todayCount ?? 0,
    upcomingCount: data?.upcomingCount ?? 0,
    isLoading,
  };
}
